import { useState } from 'react';
import { ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import type {
  ClickResolveDetails,
  PaymentMethodResult,
  Stripe,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent
} from '@stripe/stripe-js';
import { useSelector } from 'react-redux';

import type { AppState } from 'types/app';
import type { ApplePayTheme } from 'types/applePay';
import type { PurchaseState } from 'types/checkout';
import type { ExternalPaymentBillingAddress, ExternalPaymentShippingAddress, PostPurchaseDataParams } from 'types/stripe';
import { logError } from 'middleware/logger';
import { placeOrder } from 'apis/checkout';
import { DIGITAL_GC_ONLY_CART } from 'constants/cartTypes';
import { STRIPE_BUTTON_THEMES } from 'constants/stripe';
import { mapProductsToLineItemIds, mapProductsToLineItems, mapStripeShippingRates } from 'helpers/stripe';
import { toStripePrice } from 'helpers/currency';
import useMartyContext from 'hooks/useMartyContext';

interface ApplePayButtonProps {
  isGuest: boolean;
  purchaseState: PurchaseState;
  stripe: Stripe | null;
  theme: ApplePayTheme;
  onReady: () => void;
  onClose?: () => void;
  updatePurchase: (reqData: PostPurchaseDataParams) => Promise<PurchaseState>;
}

export const ApplePayButton = ({ purchaseState, stripe, theme, onReady, onClose, updatePurchase }: ApplePayButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const elements = useElements();
  const { marketplace } = useMartyContext();
  const cookies = useSelector((state: AppState) => state.cookies);
  const isEGCOnly = purchaseState.cartType === DIGITAL_GC_ONLY_CART;

  const checkoutElementOptions: StripeExpressCheckoutElementOptions = {
    buttonHeight: STRIPE_BUTTON_THEMES[theme].height,
    buttonTheme: {
      applePay: STRIPE_BUTTON_THEMES[theme].theme
    },
    buttonType: {
      applePay: STRIPE_BUTTON_THEMES[theme].type
    },
    paymentMethods: {
      amazonPay: 'never',
      applePay: 'always',
      googlePay: 'never',
      link: 'never',
      paypal: 'never'
    }
  };

  const onCancel = async () => {
    setIsLoading(true);
    setIsDialogOpen(false);
    setIsLoading(false);
  };

  const onClickPay = async (stripeEvent: StripeExpressCheckoutElementClickEvent) => {
    setIsDialogOpen(true);

    const options: ClickResolveDetails = {
      billingAddressRequired: true,
      emailRequired: true,
      phoneNumberRequired: true,
      shippingAddressRequired: true,
      shippingRates: mapStripeShippingRates(purchaseState)
    };

    stripeEvent.resolve(options);
  };

  const onShippingAddressChange = async (stripeEvent: StripeExpressCheckoutElementShippingAddressChangeEvent) => {
    if (!stripe || !elements) {
      return;
    }

    const { address, name } = stripeEvent;

    const shippingAddress: ExternalPaymentShippingAddress = {
      potentialMailingAddress: {
        city: address.city,
        countryCode: address.country,
        postalCode: address.postal_code,
        stateOrRegion: address.state
      },
      recipientName: name
    };

    const reqData: PostPurchaseDataParams = {
      ...purchaseState,
      externalPaymentMethod: { shippingAddress }
    };

    try {
      const purchase = await updatePurchase(reqData);

      if (purchase) {
        const updatedTotal = toStripePrice(purchase.chargeSummary.grandTotal);

        if (updatedTotal <= 0) {
          stripeEvent.reject();
          return;
        }

        elements.update({ amount: updatedTotal });

        stripeEvent.resolve({
          lineItems: mapProductsToLineItems(purchase),
          shippingRates: mapStripeShippingRates(purchase)
        });
      } else {
        logError('No purchase reject');
        stripeEvent.reject();
      }
    } catch (e) {
      logError('Failed to configure Stripe shipping address', e);
      stripeEvent.reject();
      return;
    }
  };

  const onShippingRateChange = async (stripeEvent: StripeExpressCheckoutElementShippingRateChangeEvent) => {
    if (!elements) {
      return;
    }

    const reqData: PostPurchaseDataParams = {
      ...purchaseState,
      shipmentOptionId: stripeEvent.shippingRate.id,
      shipmentOptionLineItemIds: mapProductsToLineItemIds(purchaseState?.productList)
    };

    try {
      const purchase = await updatePurchase(reqData);

      if (purchase) {
        const updatedTotal = toStripePrice(purchase.chargeSummary.grandTotal);

        if (updatedTotal <= 0) {
          stripeEvent.reject();
          return;
        }

        elements.update({ amount: updatedTotal });

        stripeEvent.resolve({
          lineItems: mapProductsToLineItems(purchase),
          shippingRates: mapStripeShippingRates(purchase)
        });
      } else {
        stripeEvent.reject();
      }
    } catch (e) {
      logError('Failed to configure Stripe shipping rate', e);
      stripeEvent.reject();
      return;
    }
  };

  const onConfirm = async (stripeEvent: StripeExpressCheckoutElementConfirmEvent) => {
    setIsLoading(true);

    if (!stripe || !elements) {
      logError('Failed to confirm Stripe payment', stripeEvent);
      stripeEvent.paymentFailed({ reason: 'fail' });
      setIsLoading(false);
      return;
    }

    const stateValidation = await elements.submit();

    if (stateValidation.error) {
      logError('Failed to validate Stripe payment', stateValidation);
      stripeEvent.paymentFailed({ reason: 'fail' });
      setIsLoading(false);
      return;
    }

    if (!stripeEvent.billingDetails) {
      logError('Invalid Stripe billing details');
      stripeEvent.paymentFailed({ reason: 'invalid_billing_address' });
      setIsLoading(false);
      return;
    }

    if (!isEGCOnly && !stripeEvent.shippingAddress) {
      logError('Invalid Stripe shipping address');
      stripeEvent.paymentFailed({ reason: 'invalid_shipping_address' });
      setIsLoading(false);
      return;
    }

    const paymentMethodResult: PaymentMethodResult = await stripe.createPaymentMethod({ elements });

    if (paymentMethodResult.error || !paymentMethodResult.paymentMethod?.id) {
      logError('Failed to create Stripe payment method', paymentMethodResult.error);
      stripeEvent.paymentFailed({ reason: 'invalid_payment_data' });
      setIsLoading(false);
      return;
    }

    const { paymentMethod } = paymentMethodResult;

    const reqData: PostPurchaseDataParams = {
      ...purchaseState,
      externalPaymentMethod: { paymentMethodId: paymentMethod.id, paymentMethodProvider: 'StripeApplePay' }
    };

    // Only need to set a shipping address for physical orders
    if (!isEGCOnly && reqData.externalPaymentMethod && stripeEvent.shippingAddress) {
      const { name, phone = '' } = stripeEvent.billingDetails;
      const { address } = stripeEvent.shippingAddress;

      const shippingAddress: ExternalPaymentShippingAddress = {
        phone,
        potentialMailingAddress: {
          addressLine1: address.line1,
          addressLine2: address.line2,
          city: address.city,
          countryCode: address.country,
          postalCode: address.postal_code,
          stateOrRegion: address.state
        },
        recipientName: name
      };

      reqData.externalPaymentMethod.shippingAddress = shippingAddress;
    }

    if (reqData.externalPaymentMethod && stripeEvent.billingDetails) {
      const { address, name } = stripeEvent.billingDetails;

      const billingAddress: ExternalPaymentBillingAddress = {
        addressLine1: address.line1,
        addressLine2: address.line2,
        city: address.city,
        countryCode: address.country,
        fullName: name,
        postalCode: address.postal_code,
        stateOrRegion: address.state
      };

      reqData.externalPaymentMethod.billingAddress = billingAddress;
    }

    try {
      const purchase = await updatePurchase(reqData);

      if (!purchase) {
        logError('Failed to configure purchase on confirm');
        setIsLoading(false);
        return;
      }

      const { versionNumber } = purchase;

      const { payload: orderResponse } = await placeOrder(
        { url: marketplace.api.mafia.url },
        cookies,
        purchase.purchaseId,
        versionNumber,
        false // always false for non-CC purchase
      );

      if (!orderResponse) {
        logError('Failed to place order');
        setIsLoading(false);
        return;
      }

      const orderId = orderResponse.orderId || orderResponse.digitalOrderId;
      const constraintViolations = orderResponse.constraintViolations || [];

      if (constraintViolations.length > 0 || !orderId) {
        logError('Purchase finalized with constraint violations or missing order ID');
        setIsLoading(false);
        return;
      }

      if (theme === 'cart') {
        onClose?.();
      }

      window.location.href = `https://zappos.com/kratos/checkout/${purchase.purchaseId}/confirmation`;
    } catch (error) {
      logError('Failed to finalize purchase', error);
      setIsLoading(false);
      return;
    }
  };

  const onElementReady = () => {
    setIsLoading(false);
    onReady();
  };

  return (
    <div data-testid="applePayButtonContainer">
      {isDialogOpen && (
        <div className="bg-black fixed bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-opacity-80">
          {isLoading && <div className="border-white border-t-transparent relative bottom-20 h-8 w-8 animate-spin rounded-full border-2" />}
        </div>
      )}
      <ExpressCheckoutElement
        options={checkoutElementOptions}
        onCancel={onCancel}
        onClick={onClickPay}
        onConfirm={onConfirm}
        onEscape={onCancel}
        onReady={onElementReady}
        onShippingAddressChange={onShippingAddressChange}
        onShippingRateChange={onShippingRateChange}
      />
    </div>
  );
};
